import React, { useState, useRef } from "react";
import { Modal, ModalHeader, ModalBody, Spinner, Badge } from "reactstrap";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import numeral from "numeral";
import usePAExposureDetail from "../query/hooks/usePAExposureDetail";

const PLACEMENT_COLORS = {
  stadium_signage: '#5298C5',
  stadium_led_board: '#379f8b',
  stadium_pitch_board: '#ebbc40',
  athlete_jersey_front: '#972BA0',
  athlete_jersey_arm: '#A01049',
  athlete_jersey_back: '#652847',
  field_surface: '#a09ea8',
  interview_backdrop: '#FF9F40',
  screen_overlay: '#4BC0C0',
  sports_equipment: '#FF6384',
  other: '#C9CBCF',
};

const formatScreenShare = (seconds) => {
  if (!seconds || seconds === 0) return "0s";
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

const ExposureImage = ({ item }) => {
  const containerRef = useRef(null);
  const [imgSize, setImgSize] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);

  const hasBbox = item.bbox_x1 || item.bbox_y1 || item.bbox_x2 || item.bbox_y2;
  const placementCategory = item.plc_category || item.placement_category;
  const color = PLACEMENT_COLORS[placementCategory] || '#5298C5';

  const handleImageLoad = (e) => {
    setImgSize({ width: e.target.clientWidth, height: e.target.clientHeight });
    setNaturalSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <img
        src={item.image_url}
        alt={`Track ${item.track_id}`}
        onLoad={handleImageLoad}
        style={{
          width: '100%',
          aspectRatio: '16/9',
          objectFit: 'cover',
          borderRadius: '6px',
          display: 'block',
          backgroundColor: 'var(--background-secondary)',
        }}
      />
      {hasBbox && imgSize && naturalSize && naturalSize.width > 0 && (
        (() => {
          const scaleX = imgSize.width / naturalSize.width;
          const scaleY = imgSize.height / naturalSize.height;
          const left = item.bbox_x1 * scaleX;
          const top = item.bbox_y1 * scaleY;
          const width = (item.bbox_x2 - item.bbox_x1) * scaleX;
          const height = (item.bbox_y2 - item.bbox_y1) * scaleY;
          return (
            <div style={{
              position: 'absolute',
              left: `${left}px`,
              top: `${top}px`,
              width: `${width}px`,
              height: `${height}px`,
              border: `2px solid ${color}`,
              backgroundColor: `${color}22`,
              borderRadius: '2px',
              pointerEvents: 'none',
            }} />
          );
        })()
      )}
    </div>
  );
};

const KpiTile = ({ label, value, sublabel, color }) => (
  <div style={{
    padding: '0.85rem 1rem',
    borderRadius: '8px',
    backgroundColor: 'var(--background-secondary)',
    borderLeft: `3px solid ${color}`,
  }}>
    <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>
      {label}
    </div>
    <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '1.15rem' }}>
      {value}
    </div>
    {sublabel && (
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', marginTop: '0.15rem' }}>
        {sublabel}
      </div>
    )}
  </div>
);

const ExposureDetailModal = ({ isOpen, toggle, sponsorObjectId, videoId, groupLabel, selectedCurrency }) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePAExposureDetail(isOpen ? sponsorObjectId : null, isOpen ? videoId : null, 1);

  const rows = data?.data || [];
  const summary = data?.summary || {};
  const currencyMultiplier = selectedCurrency?.value || 1;
  const currency = selectedCurrency?.currency || 'NOK';

  const attributedValueLocal = Math.round((summary.attributed_value || 0) * currencyMultiplier);
  const durationSec = summary.duration_seconds || 0;
  const reach = summary.reach || 0;
  const cpmRate = summary.cpm_rate || 0;
  const cpmModifier = summary.cpm_modifier || 1;
  const sharePct = (summary.duration_share || 0) * 100;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle} style={{ backgroundColor: 'var(--background-primary)', borderColor: 'var(--border-color)' }}>
        <div>
          <span style={{ color: 'var(--text-primary)' }}>{summary.sponsor_object_name || t("Exposures")}</span>
          {groupLabel && (
            <small className="d-block" style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{groupLabel}</small>
          )}
        </div>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: 'var(--background-primary)' }}>
        {/* KPI tiles */}
        {summary.sponsor_object_name && (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '0.75rem',
              marginBottom: '1.5rem',
            }}>
              <KpiTile
                label={t("Attributed Value")}
                value={`${numeral(attributedValueLocal).format('0,0')} ${currency}`}
                sublabel={t("This broadcast")}
                color="#5298C5"
              />
              <KpiTile
                label={t("Duration Share")}
                value={`${sharePct.toFixed(1)}%`}
                sublabel={`${formatScreenShare(durationSec)} of ${formatScreenShare(summary.broadcast_duration_seconds || 0)}`}
                color="#379f8b"
              />
              <KpiTile
                label={t("Reach")}
                value={numeral(reach).format('0,0')}
                sublabel={t("Viewers")}
                color="#ebbc40"
              />
              <KpiTile
                label={t("CPM")}
                value={`${numeral(cpmRate).format('0,0.00')}`}
                sublabel={`${t("Modifier")} ${cpmModifier.toFixed(2)}`}
                color="#972BA0"
              />
            </div>

          </>
        )}

        {/* Example images */}
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
          {t("Example detections")}
        </div>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner size="sm" />
          </div>
        ) : rows.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>{t("No data available")}</p>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '0.85rem',
          }}>
            {rows.map((item, idx) => (
              <div key={idx} style={{
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                backgroundColor: 'var(--background-secondary)',
              }}>
                {item.image_url ? (
                  <ExposureImage item={item} />
                ) : (
                  <div style={{
                    width: '100%',
                    aspectRatio: '16/9',
                    backgroundColor: 'var(--background-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <small style={{ color: 'var(--text-secondary)' }}>No image</small>
                  </div>
                )}
                <div style={{ padding: '0.65rem 0.75rem' }}>
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <Badge style={{
                      fontSize: '0.7rem',
                      textTransform: 'capitalize',
                      backgroundColor: PLACEMENT_COLORS[item.plc_category] || '#6c757d',
                      border: 'none',
                    }}>
                      {(item.plc_category || 'unknown').replace(/_/g, ' ')}
                    </Badge>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      {item.track_duration_seconds > 0 ? formatScreenShare(item.track_duration_seconds) : '-'}
                    </span>
                  </div>
                  {item.team_id && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      {item.team_id.replace(/_/g, ' ')}
                    </div>
                  )}
                  {item.person_name && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                      {item.person_name}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  selectedCurrency: state.currency.selectedCurrency,
});

export default connect(mapStateToProps)(ExposureDetailModal);
